import env from '../environment';
import { ogImageFor, imagePreloadLink } from '../components/ResponsiveImage';

export const ogImage = ({
  src,
}: {
  src: string;
}): { url: string; width?: number; height?: number } => {
  const generated = ogImageFor({ src });
  if (generated) {
    return {
      url: `${env.webUrl}${generated.path}`,
      width: generated.width,
      height: generated.height,
    };
  }
  return { url: /^https?:\/\//.test(src) ? src : `${env.webUrl}${src}` };
};

export interface BasicProps {
  loaderData?:
  | {
    title?: string;
    pathname?: string;
  }
  | undefined;
}

interface SeoProps {
  loaderData?:
  | {
    pathname?: string;
    seo: {
      title?: string;
      description?: string;
      keywords?: string;
      type?: 'article' | 'website';
      image?: {
        url?: string;
        width?: number;
        height?: number;
      };
      schema?: Record<string, unknown>;
      preloadImage?: {
        src: string;
        sizes: string;
      };
    };
  }
  | undefined;
}

type Props = BasicProps | SeoProps;

function isSeoLoaderData(data: Props['loaderData']): data is {
  seo: NonNullable<SeoProps['loaderData']>['seo'];
  pathname?: string;
} {
  return !!data && 'seo' in data;
}

function isBasicLoaderData(
  data: Props['loaderData']
): data is { title?: string; pathname?: string } {
  return !!data && ('title' in data || 'pathname' in data) && !('seo' in data);
}


export const DEFAULT_TITLE =
  'Stark Crawl - Web crawling & structured data for market research';

const DEFAULT_OG_IMAGE = ogImage({ src: '/images/hero.png' });

export const seoHead = ({ loaderData }: SeoProps) => {
  const title = loaderData?.seo.title ?? DEFAULT_TITLE;
  const description = loaderData?.seo.description;
  const url = loaderData?.pathname
    ? `${env.webUrl}${loaderData.pathname}`
    : undefined;

  const image = loaderData?.seo.image?.url ? loaderData.seo.image : DEFAULT_OG_IMAGE;
  
  const preload = loaderData?.seo.preloadImage
    ? imagePreloadLink(loaderData.seo.preloadImage)
    : undefined;

  return {
    meta: [
      { title },
      ...[
        {
          name: 'description',
          content: description,
        },
        {
          name: 'keywords',
          content: loaderData?.seo.keywords,
        },
        // Open Graph consumers (Facebook, LinkedIn, Slack, WhatsApp) only
        // read `property` attributes, not `name`.
        {
          property: 'og:site_name',
          content: 'Stark Crawl',
        },
        {
          property: 'og:type',
          content: loaderData?.seo.type ?? 'website',
        },
        {
          property: 'og:title',
          content: title,
        },
        {
          property: 'og:url',
          content: url,
        },
        {
          property: 'og:description',
          content: description,
        },
        {
          property: 'og:image',
          content: image.url,
        },
        {
          property: 'og:image:width',
          content: image.width ? String(image.width) : undefined,
        },
        {
          property: 'og:image:height',
          content: image.height ? String(image.height) : undefined,
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:title',
          content: title,
        },
        {
          name: 'twitter:description',
          content: description,
        },
        {
          name: 'twitter:image',
          content: image.url,
        },
      ].filter((meta) => meta.content),
    ],
    links: [
      ...(url ? [{ rel: 'canonical', href: url }] : []),
      ...(preload ? [preload] : []),
    ],
    scripts: [
      {
        id: 'page-schema',
        type: 'application/ld+json',
        content: loaderData?.seo.schema
          ? JSON.stringify(loaderData.seo.schema)
          : undefined,
      },
    ].filter((script) => script.content),
  };
};

export const basicHead = ({ loaderData }: Props) =>
  isSeoLoaderData(loaderData)
    ? seoHead({ loaderData })
    : isBasicLoaderData(loaderData)
      ? {
        meta: [
          {
            title: `${loaderData.title ?? 'Not Found'}`,
          },
          ...[
            {
              property: 'og:title',
              content: `${loaderData.title ?? 'Not Found'}`,
            },
            {
              property: 'og:url',
              content: loaderData.pathname
                ? `${env.webUrl}${loaderData.pathname}`
                : undefined,
            },
          ].filter((meta) => meta.content),
        ],
        links: [
          {
            rel: 'canonical',
            href: loaderData.pathname
              ? `${env.webUrl}${loaderData.pathname}`
              : undefined,
          },
        ].filter((link) => link.href),
      }
      : {};

